'use client'

import { type ComponentType, useEffect, useState } from 'react'

import { WIDGET_LOADING_FALLBACK } from '@/features/dashboard/components/widget-loading-fallback'

type WidgetModule<P> = ComponentType<P> | { default: ComponentType<P> }

/**
 * Code-split a dashboard widget without suspending on its first mount.
 *
 * `next/dynamic` is `React.lazy`: the first render suspends and React reveals the widget on a
 * Suspense retry. Retry lanes never expire, and the dashboard's WebSocket streams commit many
 * times a second, so that retry starved for 2–7 s+ on every page load although the chunk had
 * arrived by ~0.7 s. Here the widget renders its own fallback and swaps the module in with a
 * plain state update, which React schedules like any other update.
 */
export function lazyWidget<P extends object>(load: () => Promise<WidgetModule<P>>) {
  let loaded: ComponentType<P> | null = null
  let pending: Promise<ComponentType<P>> | null = null

  const loadOnce = () =>
    (pending ??= load().then(
      (mod) => (loaded = 'default' in mod ? mod.default : mod),
      (error: unknown) => {
        // Let the next mount (e.g. WidgetErrorBoundary's Reload) fetch the chunk again.
        pending = null
        throw error
      }
    ))

  return function LazyWidget(props: P) {
    const [result, setResult] = useState<{ Component: ComponentType<P> | null; error?: unknown }>(
      () => ({ Component: loaded })
    )

    useEffect(() => {
      if (result.Component) return
      loadOnce().then(
        (Component) => setResult({ Component }),
        (error: unknown) => setResult({ Component: null, error })
      )
    }, [result.Component])

    // Chunk-load failures reach WidgetErrorBoundary, as they did through next/dynamic.
    if (result.error) throw result.error
    const { Component } = result
    return Component ? <Component {...props} /> : WIDGET_LOADING_FALLBACK
  }
}
